import { Box, Text } from '@wix/design-system';
import React, { FC, useMemo } from 'react';

import { useIsRtlText } from '../../hooks';
import { ButtonIcon } from '../ButtonIcon';
import { TextWithAutoDirection } from '../TextWithAutoDirection';

type ButtonContentProps = {
  label: string;
  sublabel?: string;
  icon?: string;
};

/**
 * Responsible for button content, including icon, title and optional subtitle.
 * Reverses direction for RTL languages.
 */
export const ButtonContent: FC<ButtonContentProps> = ({ label, sublabel, icon }) => {
  // Identifying column direction by label. Label and sub-label can have have individual directions though.
  const isRtl = useIsRtlText(label);

  const buttonBlocks = useMemo(() => {
    const items = [
      <ButtonIcon key="icon" icon={icon} />,
      <Box key="labels" direction="vertical">
        <Box margin={isRtl ? '0 0 0 SP3' : '0 SP3 0 0'} direction="vertical">
          <Text weight="normal" skin="primary" dataHook="label">
            <TextWithAutoDirection text={label} />
          </Text>
        </Box>
        {sublabel && (
          <Text size="small" weight="thin" dataHook="sublabel">
            <TextWithAutoDirection text={sublabel} />
          </Text>
        )}
      </Box>,
    ];

    return isRtl ? items.reverse() : items;
  }, [isRtl, label, sublabel, icon]);

  return (
    <Box padding="SP2 SP3" gap="SP3" align={isRtl ? 'right' : 'left'}>
      {buttonBlocks}
    </Box>
  );
};
